import { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  CalendarClock,
  AlertTriangle,
  Check,
  Lock,
  PencilLine,
  Save,
  Send,
  SendHorizontal,
  X,
} from "lucide-react";

import { Badge } from "../../../components/Badge";
import { Button } from "../../../components/Button";
import { MarkdownEditor } from "../../../components/MarkdownEditor";
import { ConfirmModal } from "../../../components/ConfirmModal";
import UnsavedChangesBar from "../../../components/UnsavedChangesBar";
import { getData } from "../../../components/hooks/getData";
import { useAuth } from "../../../components/contexts/AuthContext";
import { brand } from "../../../theme/theme";
import { ContentPreviewCard, cardShellSx, sectionLabelStyle } from "./ContentPreviewCard";
import {
  AddNetworkButton,
  CallToActionPicker,
  ManualNetworkCard,
  hasText,
  replaceLocalImage,
  revokeLocalImages,
} from "./ManualNetworkCard";
import {
  PlatformIcon,
  adaptationFormat,
  formatLabel,
  getPlatformMeta,
  isDocumentMedia,
  isImageMedia,
  mediaWarnings,
  platformLabel,
  postTextSx,
} from "./platformMeta";
import { toLocalDocument, withDocumentTitle } from "./DocumentMedia";
import { PostPhonePreview, hasPostPreview } from "./PostPreviewModal";
import { NO_CALL_TO_ACTION, callToActionOf } from "./callToAction";
import {
  buildDiffHtml,
  formatRelative,
  formatShortDate,
  getThreadComments,
  textEditorHeight,
} from "./workflowUtils";
import { postThreadMessage, threadErrorText } from "./threadActions";
import { takeHandedOffVideos } from "./videoHandoff";
import { publishingErrorText } from "../../../services/socialPublishingService";
import { uploadInlineImage } from "../../../services/contentGenerationService";

export const viewTitleSx = {
  fontWeight: 800,
  fontSize: { xs: "1.25rem", md: "1.5rem" },
  lineHeight: 1.2,
  color: "#0F172A",
  letterSpacing: "-0.01em",
};

// Left column grows with the screen (28%) so it doesn't shrink next to wide cards.
export const sidebarGridColumns = {
  xs: "1fr",
  lg: "360px minmax(0, 1fr)",
  xl: "clamp(400px, 28%, 560px) minmax(0, 1fr)",
};

export const bottomBarSx = {
  display: "flex",
  alignItems: { xs: "stretch", sm: "center" },
  flexDirection: { xs: "column", sm: "row" },
  justifyContent: "space-between",
  gap: 1.5,
  px: { xs: 2, md: 2.5 },
  py: 1.5,
  borderRadius: "14px",
  border: "1px solid #E8ECF2",
  backgroundColor: "#fff",
};

const CHANGE_TYPE_LABELS = {
  initial: "generación inicial",
  manual_edit: "edición manual",
  ai_iteration: "iteración con IA",
  revert: "vuelta a una versión anterior",
};

const linkButtonSx = {
  border: "none",
  background: "none",
  p: 0,
  fontSize: "0.8125rem",
  fontWeight: 700,
  color: brand.pink,
  cursor: "pointer",
};

const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

const versionLabel = (version) =>
  version?.is_client ? "edición del cliente" : CHANGE_TYPE_LABELS[version?.change_type] || "cambio";

/** "Post · 454 caracteres" under each network tab; a blog counts words ("2.450 palabras"). */
export const networkTabMeta = (platform, format, text) => {
  if (platform === "blog") {
    // Markdown marks (##, **, -) are not words
    const words = (text || "").split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word)).length;
    return `${words.toLocaleString("es-ES")} palabras`;
  }
  const length = (text || "").length.toLocaleString("es-ES");
  return `${formatLabel(platform, format)} · ${length} caracteres`;
};

/**
 * One box for all the networks of a piece: tabs on top, the selected network below
 * and its mobile preview on the right (below on mobile).
 * `tabs`: [{ key, platform, label, meta, warning, onRemove }] (onRemove: "X" on hover); `addTab`: the "+" after them. The caller renders every panel
 * and hides the inactive ones, so uploads and unsaved text survive a tab change.
 */
export function NetworkTabsPanel({ tabs, activeKey, onSelect, addTab = null, trailing = null, preview = null, previewCaption = "", children }) {
  const refs = useRef({});
  const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.key === activeKey));

  const focusAndPick = (index) => {
    const next = tabs[(index + tabs.length) % tabs.length];
    refs.current[next.key]?.focus();
    onSelect(next.key);
  };

  return (
    <Box data-tutorial="cpm-network-tabs" sx={cardShellSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: { xs: 1.5, md: 2.5 },
          borderBottom: "1px solid #EEF1F5",
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        <Box role="tablist" aria-label="Redes" sx={{ display: "flex", minWidth: 0, overflowX: "auto", flex: "0 1 auto" }}>
          {tabs.map((tab, index) => {
            const active = index === activeIndex;
            return (
              <Box
                key={tab.key}
                sx={{
                  position: "relative",
                  flexShrink: 0,
                  "&:hover .network-tab-remove, &:focus-within .network-tab-remove": { opacity: 1 },
                }}
              >
              <Box
                ref={(node) => {
                  refs.current[tab.key] = node;
                }}
                component="button"
                type="button"
                role="tab"
                aria-selected={active}
                tabIndex={active ? 0 : -1}
                onClick={() => onSelect(tab.key)}
                onKeyDown={(event) => {
                  if (event.key === "ArrowRight") {
                    event.preventDefault();
                    focusAndPick(index + 1);
                  } else if (event.key === "ArrowLeft") {
                    event.preventDefault();
                    focusAndPick(index - 1);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  pl: 2,
                  // Room for the "X" even while hidden: it shows up without moving anything.
                  pr: tab.onRemove ? 4.5 : 2,
                  pt: 2,
                  pb: 1.75,
                  border: "none",
                  // Same weight and size active or not: only colours change, nothing moves.
                  borderBottom: `2px solid ${active ? brand.pink : "transparent"}`,
                  background: "none",
                  font: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "border-color 0.15s",
                  "&:hover .network-tab-label": { color: "#111827" },
                  "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: -2, borderRadius: "8px" },
                }}
              >
                <PlatformIcon
                  platform={tab.platform}
                  size={36}
                  sx={active ? {} : { filter: "grayscale(0.35)", opacity: 0.85 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    className="network-tab-label"
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      fontSize: "0.9375rem",
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: active ? "#111827" : "#6B7280",
                      transition: "color 0.15s",
                    }}
                  >
                    {tab.label}
                    {tab.warning && (
                      <AlertTriangle size={15} color="#EA580C" aria-label="Hay avisos en esta red" />
                    )}
                  </Typography>
                  {tab.meta && (
                    <Typography component="span" sx={{ display: "block", fontSize: "0.8125rem", color: "#6B7280", mt: 0.25, whiteSpace: "nowrap" }}>
                      {tab.meta}
                    </Typography>
                  )}
                </Box>
              </Box>
              {tab.onRemove && (
                <IconButton
                  className="network-tab-remove"
                  size="small"
                  aria-label={`Quitar ${tab.label}`}
                  onClick={tab.onRemove}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 6,
                    transform: "translateY(-50%)",
                    p: 0.375,
                    color: "#9CA3AF",
                    opacity: 0,
                    transition: "opacity 0.15s, color 0.15s",
                    "&:hover": { color: "#DC2626", backgroundColor: "#FEF2F2" },
                    "&:focus-visible": { opacity: 1 },
                    // No hover on touch screens: always visible.
                    "@media (hover: none)": { opacity: 1 },
                  }}
                >
                  <X size={14} />
                </IconButton>
              )}
              </Box>
            );
          })}
        </Box>
        {addTab && <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{addTab}</Box>}
        {trailing && (
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5, py: 1.5, flexShrink: 0 }}>{trailing}</Box>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: preview ? { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) clamp(340px, 30%, 440px)" } : "minmax(0, 1fr)",
          alignItems: "stretch",
        }}
      >
        <Box role="tabpanel" sx={{ minWidth: 0, p: { xs: 2, md: 3 } }}>
          {children}
        </Box>
        {preview && (
          <Box
            sx={{
              minWidth: 0,
              p: { xs: 2, md: 3 },
              backgroundColor: "#F9FAFB",
              borderLeft: { lg: "1px solid #EEF1F5" },
              borderTop: { xs: "1px solid #EEF1F5", lg: "none" },
            }}
          >
            <Box sx={{ position: { lg: "sticky" }, top: { lg: 16 }, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={sectionLabelStyle}>Vista previa</Typography>
                {previewCaption && (
                  <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>{previewCaption}</Typography>
                )}
              </Box>
              {preview}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

const cardStatus = (pieceStatus) => {
  if (pieceStatus === "client_approved") return { label: "Aprobado", color: "info" };
  if (pieceStatus === "client_review") return { label: "Pendiente", color: "pending" };
  return { label: "Borrador", color: "muted" };
};

/**
 * Step 2 — review of the piece. One card per network as the client will see
 * it; on the right, the comments of the thread and the version history
 * of the selected network. The client approves or asks for changes; the team
 * applies them by hand, compares versions and sends them back.
 * Step 1 reuses it with `showSidebar={false}`: the same cards, without comments
 * or version history.
 */
export function ReviewView({
  piece,
  adaptations,
  selectedAdaptationId,
  onSelectAdaptation,
  messages,
  tutorialVersions = null,
  isClient,
  isBusy = false,
  versionsRefreshKey = 0,
  onApprove,
  onSendToClient,
  onEditAdaptation,
  onGoToScheduling,
  onSaveText,
  onAddImages,
  onRemoveImage,
  onAddVideo = null, // (adaptationId, file, { onProgress, signal }) => Promise<item>
  onConnectNetwork = null, // (platform) => Promise<boolean>: true once the network is connected
  onChangeFormat = null, // (adaptationId, format) => Promise
  onChangeCallToAction = null, // (adaptationId, { type, url }) => Promise — Google Business post button
  onAddDocument = null, // (adaptationId, file, title) => Promise, throws on error — LinkedIn PDF
  onChangeDocumentTitle = null, // (adaptationId, title) => Promise, throws on error
  onCropImage = null, // (adaptationId, imageId, box) => Promise, throws on error
  mediaError = null, // { message, mediaId } of the last publishing error about a file
  onMessagesChanged,
  onOpenImage,
  notify,
  showSidebar = true,
  hideMainAction = false,
  onAddAdaptation = null, // (platform, text, files, { format, silent }) => Promise<adaptation | null> — "Añadir red" on a draft
  onRemoveAdaptation = null, // (adaptationId) => Promise — "X" on hover of each network tab
  onUploadingChange = null, // (boolean) => void — some card is uploading a video
  onIterate = null, // (adaptationId) => void — opens the "Regenerar con IA" dialog
  iteratingAdaptationId = null,
}) {
  const theme = useTheme();
  const { interceptorReady } = useAuth();
  const [compareBaseId, setCompareBaseId] = useState(null);
  // Unsaved text per adaptation id; the team edits right on the card.
  const [drafts, setDrafts] = useState({});
  const [isSavingText, setIsSavingText] = useState(false);
  const [savingCallToActionId, setSavingCallToActionId] = useState(null);
  const [reply, setReply] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [localRefresh, setLocalRefresh] = useState(0);
  // Networks added by hand, not saved yet: [{ key, platform, text, images, format, callToAction, savedId }].
  // `savedId`: already created by its first video, kept on screen until the upload ends.
  const [newNetworks, setNewNetworks] = useState([]);
  const [activeNewKey, setActiveNewKey] = useState(null);
  const videoUploadsRef = useRef({}); // uploads in flight per new network key
  // Updated on every change, not on render: the card changes the format and starts the upload in the same tick.
  const newNetworksRef = useRef([]);
  const updateNewNetworks = (updater) => {
    newNetworksRef.current = updater(newNetworksRef.current);
    setNewNetworks(newNetworksRef.current);
  };
  const [isSavingNetwork, setIsSavingNetwork] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  // Cards uploading a video (by adaptation id), plus new networks saved by their video.
  const [uploadingIds, setUploadingIds] = useState([]);
  const setCardUploading = (id, busy) =>
    setUploadingIds((prev) => (busy ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((other) => other !== id)));
  // Video picked on the manual page before the piece existed: its card uploads it.
  const [handedOff, setHandedOff] = useState(null);
  useEffect(() => {
    if (!piece?.id) return;
    const handoff = takeHandedOffVideos(piece.id);
    if (!handoff) return;
    setHandedOff(handoff);
    onSelectAdaptation?.(handoff.adaptationId);
  }, [piece?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [isRemoving, setIsRemoving] = useState(false);

  const status = piece?.status || "draft";
  const threadId = piece?.thread?.id || null;
  const isDraft = status === "draft";
  const inReview = status === "client_review";

  const adaptation =
    adaptations.find((a) => a.id === selectedAdaptationId) || adaptations[0] || null;
  const platformName = adaptation ? platformLabel(adaptation.platform) : "";

  const { data: versionsData, isLoading: isLoadingVersions } = getData(
    adaptation ? `content-generation/adaptations/${adaptation.id}/versions/` : "",
    interceptorReady && showSidebar && Boolean(adaptation) && !tutorialVersions,
    [adaptation?.id, versionsRefreshKey, localRefresh]
  );
  const versions = tutorialVersions ?? (Array.isArray(versionsData) ? versionsData : []);
  const currentVersion = versions[0] || null;
  const baseVersion = versions.find((version) => version.id === compareBaseId) || null;

  useEffect(() => {
    setCompareBaseId(null);
  }, [adaptation?.id]);

  const comments = useMemo(() => getThreadComments(messages), [messages]);
  const commentsListRef = useRef(null);

  // Newest comments are at the bottom: keep them in view as they arrive.
  useEffect(() => {
    const list = commentsListRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [comments.length]);

  const diff = useMemo(() => {
    if (!baseVersion) return null;
    return buildDiffHtml(baseVersion.text_snapshot, adaptation?.text ?? currentVersion?.text_snapshot, {
      addedBg: `${theme.palette.success.main}22`,
      addedText: theme.palette.success.dark,
      removedBg: `${theme.palette.error.main}1A`,
      removedText: theme.palette.error.dark,
    });
  }, [baseVersion, adaptation?.text, currentVersion, theme]);

  const handleSendReply = async () => {
    const text = reply.trim();
    if (!text || !threadId) return;
    setIsSendingReply(true);
    try {
      await postThreadMessage(threadId, text);
      setReply("");
      onMessagesChanged?.();
    } catch (err) {
      notify?.(threadErrorText(err, "No se ha podido enviar el mensaje."), true);
    } finally {
      setIsSendingReply(false);
    }
  };

  // Images are managed right on the card (uploaded/removed at once), not in text editing.
  const handleAddImages = async (item, files) => {
    const added = await onAddImages(item.id, files);
    if (added !== null) setLocalRefresh((value) => value + 1);
    return added;
  };

  const handleAddDocument = async (item, file, title) => {
    const added = await onAddDocument(item.id, file, title);
    setLocalRefresh((value) => value + 1);
    return added;
  };

  const handleChangeDocumentTitle = (item, title) => onChangeDocumentTitle(item.id, title);

  const handleAddVideo = async (item, file, options) => {
    const added = await onAddVideo(item.id, file, options);
    setLocalRefresh((value) => value + 1);
    return added;
  };

  const handleChangeFormat = (item, format) => onChangeFormat(item.id, format);
  // Google Business post button: shows on the picker itself; only an error pops up.
  const handleChangeCallToAction = async (item, callToAction) => {
    if (savingCallToActionId) return;
    setSavingCallToActionId(item.id);
    try {
      await onChangeCallToAction(item.id, callToAction);
    } catch (err) {
      notify?.(publishingErrorText(err, "No se ha podido cambiar el botón de la publicación."), true);
    } finally {
      setSavingCallToActionId(null);
    }
  };

  const handleRemoveImage = async (item, image) => {
    const removed = await onRemoveImage(item.id, image.id);
    if (removed !== null) setLocalRefresh((value) => value + 1);
  };

  const draftText = (item) => drafts[item.id] ?? item.text ?? "";
  const isDirty = (item) => drafts[item.id] !== undefined && drafts[item.id] !== (item.text || "");
  const hasUnsavedText = adaptations.some(isDirty);

  // The AI rewrites the saved text, so unsaved edits must be saved or discarded first.
  const handleIterate = (item) => {
    if (isDirty(item)) {
      notify?.("Guarda o descarta tus cambios antes de regenerar con IA.", true);
      return;
    }
    onIterate(item.id);
  };

  // Once the AI is done the card shows the new text, not an older draft.
  const prevIteratingId = useRef(null);
  useEffect(() => {
    if (prevIteratingId.current && prevIteratingId.current !== iteratingAdaptationId) {
      discardDraft(prevIteratingId.current);
    }
    prevIteratingId.current = iteratingAdaptationId;
  }, [iteratingAdaptationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const discardDraft = (id) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  // Una nueva versión por cada red editada, todas de una vez. True si se han guardado todas.
  const handleSaveAllText = async ({ silent = false } = {}) => {
    if (!onSaveText || !hasUnsavedText) return true;
    if (isSavingText) return false;
    setIsSavingText(true);
    try {
      const dirty = adaptations.filter(isDirty);
      const results = await Promise.all(dirty.map((item) => onSaveText(item.id, drafts[item.id], { silent })));
      dirty.forEach((item, index) => {
        if (results[index] !== null) discardDraft(item.id);
      });
      setLocalRefresh((value) => value + 1);
      return results.every((result) => result !== null);
    } finally {
      setIsSavingText(false);
    }
  };

  // Lo editado se guarda antes de enviar.
  const handleSaveAndSend = async () => {
    if (await handleSaveAllText()) onSendToClient();
  };

  const pendingNetworks = newNetworks.filter((item) => !item.savedId);
  const isUploadingVideo = uploadingIds.length > 0 || newNetworks.some((item) => item.uploading);
  useEffect(() => {
    onUploadingChange?.(isUploadingVideo);
  }, [isUploadingVideo]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchNewNetwork = (key, update) =>
    updateNewNetworks((prev) => prev.map((item) => (item.key === key ? { ...item, ...update(item) } : item)));

  const startNewNetwork = (platform) => {
    const key = `new-${platform}`;
    updateNewNetworks((prev) => [...prev, { key, platform, text: "", images: [], format: "post", callToAction: NO_CALL_TO_ACTION, savedId: null }]);
    setActiveNewKey(key);
  };

  const dropNewNetwork = (key) => {
    updateNewNetworks((prev) => {
      revokeLocalImages(prev.find((item) => item.key === key)?.images);
      return prev.filter((item) => item.key !== key);
    });
    setActiveNewKey((current) => (current === key ? null : current));
  };

  // Creates the network (text, images, format and Google Business button); its videos go afterwards.
  // Returns the adaptation or null.
  const createNewNetwork = async (item, { silent = false } = {}) => {
    const saved = await onAddAdaptation(
      item.platform,
      item.text.trim(),
      item.images.filter(isImageMedia).map((image) => image.file),
      { format: item.format, silent }
    );
    const localDocument = item.images.find(isDocumentMedia);
    if (saved && localDocument && onAddDocument) {
      try {
        await onAddDocument(saved.id, localDocument.file, localDocument.title);
      } catch {
        // The toast already says why; the network is saved without its PDF.
      }
    }
    if (saved && item.callToAction.type && onChangeCallToAction) {
      try {
        await onChangeCallToAction(saved.id, item.callToAction);
      } catch (err) {
        notify?.(publishingErrorText(err, "No se ha podido guardar el botón de la publicación."), true);
      }
    }
    return saved;
  };

  // "Guardar cambios": every network not saved yet, one by one. True if all of them were saved.
  const handleSaveNewNetworks = async ({ silent = false } = {}) => {
    if (!pendingNetworks.length) return true;
    if (isSavingNetwork) return false;
    setIsSavingNetwork(true);
    let allSaved = true;
    try {
      for (const item of pendingNetworks) {
        if (await createNewNetwork(item, { silent })) dropNewNetwork(item.key);
        else allSaved = false;
      }
    } finally {
      setIsSavingNetwork(false);
    }
    return allSaved;
  };

  // A video saves its network first and uploads to it; the card shows the progress.
  const handleNewNetworkVideo = async (key, file, options) => {
    const item = newNetworksRef.current.find((candidate) => candidate.key === key);
    if (!item) throw new Error("missing network");
    if (!hasText(item.text)) {
      throw { response: { data: { error: "Escribe el texto de la red antes de subir el vídeo." } } };
    }
    videoUploadsRef.current[key] = (videoUploadsRef.current[key] || 0) + 1;
    patchNewNetwork(key, () => ({ uploading: true }));
    try {
      let savedId = item.savedId;
      if (!savedId) {
        const saved = await createNewNetwork(item);
        if (!saved) throw { response: { data: { error: "No se ha podido guardar la red." } } };
        savedId = saved.id;
        patchNewNetwork(key, () => ({ savedId, savedFormat: item.format }));
      } else if (onChangeFormat && item.format !== item.savedFormat) {
        // Format changed after a failed upload (retry): the server checks the video against it.
        await onChangeFormat(savedId, item.format);
        patchNewNetwork(key, () => ({ savedFormat: item.format }));
      }
      const video = await onAddVideo(savedId, file, options);
      // Once its last video is in, the saved network takes the tab.
      setTimeout(() => {
        if (videoUploadsRef.current[key] || !newNetworksRef.current.some((other) => other.key === key)) return;
        dropNewNetwork(key);
        onSelectAdaptation(savedId);
      }, 0);
      return video;
    } finally {
      videoUploadsRef.current[key] -= 1;
      if (!videoUploadsRef.current[key]) patchNewNetwork(key, () => ({ uploading: false }));
    }
  };

  const handleConfirmRemove = async () => {
    if (!removeTarget || isRemoving) return;
    setIsRemoving(true);
    try {
      await onRemoveAdaptation(removeTarget.id);
      setRemoveTarget(null);
    } finally {
      setIsRemoving(false);
    }
  };

  // Free the local previews of an unsaved network when leaving.
  useEffect(() => () => newNetworksRef.current.forEach((item) => revokeLocalImages(item.images)), []);

  if (!adaptation) {
    return (
      <Typography sx={{ color: "#6B7280", p: 2 }}>
        Este contenido aún no tiene redes generadas.
      </Typography>
    );
  }

  // Only the team edits; the client asks for changes on the thread.
  const canEditByHand = !isClient && Boolean(onSaveText);
  const uploadPieceImage = (file) => uploadInlineImage(piece.config, file);
  const canAddImages = !isClient && Boolean(onAddImages);
  const canRemoveImages = !isClient && Boolean(onRemoveImage);
  const canAddVideos = !isClient && Boolean(onAddVideo);
  const canChangeFormat = !isClient && Boolean(onChangeFormat);
  const canChangeCallToAction = !isClient && Boolean(onChangeCallToAction);
  const canAddDocuments = !isClient && Boolean(onAddDocument);
  const canRemoveNetworks = !isClient && Boolean(onRemoveAdaptation) && adaptations.length > 1;
  const activeNewNetwork = newNetworks.find((item) => item.key === activeNewKey) || null;
  const savedNewIds = newNetworks.map((item) => item.savedId).filter(Boolean);

  // Main action of the step, at the end of the side column.
  let mainAction = null;
  if (hideMainAction) {
    mainAction = null;
  } else if (status === "client_approved") {
    if (!isClient && onGoToScheduling) {
      mainAction = (
        <Button onClick={onGoToScheduling}>
          <CalendarClock size={16} /> Ir a programación
        </Button>
      );
    }
  } else if (inReview) {
    mainAction = (
      <Button onClick={onApprove} disabled={isBusy}>
        <Check size={16} /> {isClient ? "Aprobar contenido" : "Aprobar por el cliente"}
      </Button>
    );
  } else if (!isClient && onSendToClient) {
    mainAction = (
      <Button
        data-tutorial="cpm-send-to-client"
        onClick={handleSaveAndSend}
        // Not disabled while saving (it would flicker): handleSaveAllText ignores a second click.
        disabled={isBusy || pendingNetworks.length > 0 || isUploadingVideo}
      >
        <Send size={16} /> Enviar al cliente
      </Button>
    );
  }

  const renderBodyOverride = (item) => {
    if (item.id === adaptation.id && diff) {
      return (
        <>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={sectionLabelStyle}>
                v{baseVersion.version_number} → v{currentVersion?.version_number}
              </Typography>
              <Badge color="done">
                {diff.changes} {diff.changes === 1 ? "cambio" : "cambios"}
              </Badge>
            </Box>
            <IconButton size="small" aria-label="Quitar comparación" onClick={() => setCompareBaseId(null)}>
              <X size={15} />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, fontSize: "0.75rem", color: "#6B7280" }}>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <Box component="span" sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: `${theme.palette.error.main}33` }} />
              eliminado
            </Box>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <Box component="span" sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: `${theme.palette.success.main}33` }} />
              añadido
            </Box>
          </Box>
          <Box
            sx={{
              fontSize: "0.875rem",
              lineHeight: 1.65,
              color: "#374151",
              wordBreak: "break-word",
              ...postTextSx(adaptation.platform),
            }}
            dangerouslySetInnerHTML={{ __html: diff.html }}
          />
        </>
      );
    }
    return null;
  };

  const renderTextEditor = (item, isPublished) => {
    if (!canEditByHand || isPublished || (item.id === adaptation.id && diff)) return null;
    return (
      <MarkdownEditor
        key={`review-edit-${item.id}`}
        value={draftText(item)}
        onChange={(text) => setDrafts((prev) => ({ ...prev, [item.id]: text }))}
        minHeight="198px"
        initialHeight={textEditorHeight(item.platform)} // the corner handle resizes it
        resizable
        hideBorder // the card's frame wraps the editor and its media
        placeholder="Escribe el contenido..."
        allowHeadings={item.platform === "blog"}
        allowImages={getPlatformMeta(item.platform).inlineImages}
        onImageUpload={uploadPieceImage}
        onIterate={onIterate ? () => handleIterate(item) : undefined}
        isIterating={iteratingAdaptationId === item.id}
        iterateLabel="Regenerar con IA"
        toolbarActions={
          item.platform === "google_business_profile" && canChangeCallToAction ? (
            <CallToActionPicker
              value={callToActionOf(item)}
              busy={savingCallToActionId === item.id}
              onChange={(callToAction) => handleChangeCallToAction(item, callToAction)}
            />
          ) : null
        }
      />
    );
  };

  // Something to fix before scheduling: the tab shows a warning.
  const hasWarning = (item) => {
    if (isClient || item.social_post?.status === "published") return false;
    const media = item.images || [];
    return (
      item.social_post?.status === "error" ||
      (mediaError?.mediaId != null && media.some((image) => image.id === mediaError.mediaId)) ||
      mediaWarnings(item.platform, adaptationFormat(item), media).length > 0
    );
  };

  const tabs = [
    ...adaptations.filter((item) => !savedNewIds.includes(item.id)).map((item) => ({
      key: String(item.id),
      platform: item.platform,
      label: platformLabel(item.platform),
      meta: networkTabMeta(item.platform, adaptationFormat(item), draftText(item)),
      warning: hasWarning(item),
      onRemove: canRemoveNetworks && item.social_post?.status !== "published" ? () => setRemoveTarget(item) : null,
    })),
    ...newNetworks.map((item) => ({
      key: item.key,
      platform: item.platform,
      label: platformLabel(item.platform),
      meta: item.uploading ? "Subiendo vídeo" : item.savedId ? "Guardada" : "Sin guardar",
      // Saved by a video that failed: its X only frees the tab (the network stays, as its own tab).
      onRemove: isSavingNetwork || item.uploading ? null : () => dropNewNetwork(item.key),
    })),
  ];
  const activeKey = activeNewNetwork ? activeNewNetwork.key : String(adaptation.id);
  const handleSelectTab = (key) => {
    if (newNetworks.some((item) => item.key === key)) {
      setActiveNewKey(key);
      return;
    }
    const item = adaptations.find((candidate) => String(candidate.id) === key);
    if (!item) return;
    setActiveNewKey(null);
    onSelectAdaptation(item.id);
  };

  const previewItem = activeNewNetwork
    ? {
        platform: activeNewNetwork.platform,
        text: activeNewNetwork.text,
        images: activeNewNetwork.images,
        format: activeNewNetwork.format,
        callToAction: activeNewNetwork.callToAction,
      }
    : {
        platform: adaptation.platform,
        text: draftText(adaptation),
        images: adaptation.images || [],
        format: adaptationFormat(adaptation),
        callToAction: callToActionOf(adaptation),
      };
  // A network without phone preview (blog) gets no side column: the editor takes the whole width.
  const hasPreview = hasPostPreview(previewItem.platform);
  const preview = hasPreview ? (
    <PostPhonePreview
      platform={previewItem.platform}
      text={previewItem.text}
      images={previewItem.images}
      accountName={piece?.client_name}
      format={previewItem.format}
      callToAction={previewItem.callToAction}
    />
  ) : null;
  const previewCaption = hasPreview
    ? `${platformLabel(previewItem.platform)} · ${formatLabel(previewItem.platform, previewItem.format)}`
    : "";

  const canAddNetwork = Boolean(onAddAdaptation) && !isClient;
  // Step 1 has no side column: the main action goes on the tabs row.
  // Saves the edited texts and the new networks together.
  const isSavingChanges = isSavingText || isSavingNetwork;
  const handleSaveChanges = async (options) =>
    (await handleSaveAllText(options)) && (await handleSaveNewNetworks(options));
  const cannotSaveChanges = isSavingChanges || pendingNetworks.some((item) => !hasText(item.text));
  // Step 1 of a draft always offers "Guardar borrador", like the manual page; it saves whatever is pending.
  const showSaveDraft = !showSidebar && !hideMainAction && !isClient && isDraft && Boolean(onSaveText);
  const handleSaveDraft = async () => {
    if (isSavingChanges) return;
    // Only "Borrador guardado": the saves inside it don't toast.
    if (await handleSaveChanges({ silent: true })) notify?.("Borrador guardado");
  };
  let saveTextAction = null;
  if (showSaveDraft) {
    saveTextAction = (
      // Same look while saving (no flicker); handleSaveDraft ignores a second click.
      <Button variant="dark" onClick={handleSaveDraft} disabled={pendingNetworks.some((item) => !hasText(item.text))}>
        <Save size={16} /> Guardar borrador
      </Button>
    );
  }
  const unsavedBar = showSaveDraft ? null : (
    <UnsavedChangesBar
      count={adaptations.filter(isDirty).length + pendingNetworks.length}
      saving={isSavingChanges}
      onDiscard={() => {
        setDrafts({});
        updateNewNetworks((list) => list.filter((item) => item.savedId));
      }}
      onSave={() =>
        cannotSaveChanges
          ? notify?.("Escribe el texto de las redes nuevas antes de guardar.", true)
          : handleSaveChanges()
      }
    />
  );
  const tabsMainAction = !showSidebar ? mainAction : null;
  const tabsTrailing =
    saveTextAction || tabsMainAction ? (
      <>
        {saveTextAction}
        {tabsMainAction}
      </>
    ) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minHeight: 0, maxWidth: { md: 1440 } }}>
      {unsavedBar}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: showSidebar ? sidebarGridColumns : "minmax(0, 1fr)",
          gap: 2,
          alignItems: "start",
        }}
      >
        <NetworkTabsPanel
          tabs={tabs}
          activeKey={activeKey}
          onSelect={handleSelectTab}
          addTab={
            canAddNetwork ? (
              <AddNetworkButton
                usedPlatforms={[...adaptations, ...newNetworks].map((item) => item.platform)}
                onAdd={startNewNetwork}
              />
            ) : null
          }
          trailing={tabsTrailing}
          preview={preview}
          previewCaption={previewCaption}
        >
          {/* Every network stays mounted (hidden): video uploads and unsaved text survive a tab change. */}
          {adaptations.map((item) => {
            const isActive = String(item.id) === activeKey;
            // Already live on the network: the backend refuses any change too.
            const isPublished = item.social_post?.status === "published";
            return (
              <Box key={item.id} sx={{ display: isActive ? "block" : "none" }}>
                <ContentPreviewCard
                  embedded
                  title={piece?.title}
                  adaptations={[item]}
                  status={cardStatus(status)}
                  onOpenImage={onOpenImage}
                  textLines={null}
                  bodyOverride={renderBodyOverride(item)}
                  textEditor={renderTextEditor(item, isPublished)}
                  onAddImages={canAddImages && !isPublished ? handleAddImages : null}
                  onRemoveImage={canRemoveImages && !isPublished ? handleRemoveImage : null}
                  onAddVideo={canAddVideos && !isPublished ? handleAddVideo : null}
                  onConnectNetwork={canAddVideos && onConnectNetwork ? (adaptationItem) => onConnectNetwork(adaptationItem.platform) : null}
                  onChangeFormat={canChangeFormat && !isPublished ? handleChangeFormat : null}
                  onAddDocument={canAddDocuments && !isPublished ? handleAddDocument : null}
                  onChangeDocumentTitle={
                    !isClient && onChangeDocumentTitle && !isPublished ? handleChangeDocumentTitle : null
                  }
                  onCropImage={
                    !isClient && onCropImage && !isPublished
                      ? (adaptation, image, box) => onCropImage(adaptation.id, image.id, box)
                      : null
                  }
                  initialVideos={handedOff?.adaptationId === item.id ? handedOff.files : null}
                  onUploadingChange={(busy) => setCardUploading(item.id, busy)}
                  mediaError={mediaError}
                  footerNote={
                    isPublished && !isClient ? (
                      <Typography sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "0.75rem", color: "#6B7280" }}>
                        <Lock size={13} /> Publicado: el texto y las imágenes ya no se pueden editar.
                      </Typography>
                    ) : null
                  }
                  actions={
                    !isClient && !isPublished && !canEditByHand && onEditAdaptation ? (
                      <Button variant="outline" onClick={() => onEditAdaptation(item.id)}>
                        <PencilLine size={16} /> Editar texto
                      </Button>
                    ) : null
                  }
                />
              </Box>
            );
          })}
          {newNetworks.map((item) => (
            <Box key={item.key} sx={{ display: item.key === activeKey ? "block" : "none" }}>
              <ManualNetworkCard
                embedded
                platform={item.platform}
                onUploadInlineImage={uploadPieceImage}
                text={item.text}
                images={item.images}
                onTextChange={(text) => patchNewNetwork(item.key, () => ({ text }))}
                onAddImages={(localImages) =>
                  patchNewNetwork(item.key, (prev) => ({ images: [...prev.images, ...localImages] }))
                }
                onCropImage={(image, cropped) =>
                  patchNewNetwork(item.key, (prev) => ({ images: replaceLocalImage(prev.images, image, cropped) }))
                }
                onRemoveImage={(image) => {
                  revokeLocalImages([image]);
                  patchNewNetwork(item.key, (prev) => ({ images: prev.images.filter((other) => other.id !== image.id) }));
                }}
                format={item.format}
                onChangeFormat={(format) => patchNewNetwork(item.key, () => ({ format }))}
                onAddVideo={canAddVideos ? (file, options) => handleNewNetworkVideo(item.key, file, options) : null}
                callToAction={item.callToAction}
                onChangeCallToAction={
                  canChangeCallToAction ? (callToAction) => patchNewNetwork(item.key, () => ({ callToAction })) : null
                }
                onAddDocument={
                  canAddDocuments
                    ? (file, title) => patchNewNetwork(item.key, (prev) => ({ images: [...prev.images, toLocalDocument(file, title)] }))
                    : null
                }
                onChangeDocumentTitle={(title) =>
                  patchNewNetwork(item.key, (prev) => ({ images: withDocumentTitle(prev.images, title) }))
                }
              />
            </Box>
          ))}
        </NetworkTabsPanel>

        <ConfirmModal
          open={Boolean(removeTarget)}
          onClose={() => setRemoveTarget(null)}
          onConfirm={handleConfirmRemove}
          title={`Quitar ${removeTarget ? platformLabel(removeTarget.platform) : ""}`}
          description={
            <>
              Se borrarán el texto, las imágenes y las versiones de{" "}
              <strong>{removeTarget ? platformLabel(removeTarget.platform) : ""}</strong>. ¿Quieres quitar esta red?
            </>
          }
          confirmLabel="Quitar red"
          loadingLabel="Quitando..."
          loading={isRemoving}
          tone="warning"
        />

        {/* ── Left: comments and versions (after the cards on mobile) ── */}
        {showSidebar && (
        <Box
          data-tutorial="cpm-review-comments"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 0,
            order: { lg: -1 },
            position: { lg: "sticky" },
            top: { lg: 0 },
          }}
        >
          <Box sx={cardShellSx}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.75, borderBottom: "1px solid #F1F5F9" }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A" }}>
                Comentarios
              </Typography>
            </Box>

            <Box ref={commentsListRef} sx={{ minHeight: 160, maxHeight: { xs: 260, lg: 300 }, overflowY: "auto" }}>
              {comments.length === 0 ? (
                <Typography sx={{ px: 2.5, py: 3, fontSize: "0.875rem", color: "#6B7280", textAlign: "center" }}>
                  {isDraft
                    ? "El cliente podrá comentar cuando le envíes el contenido."
                    : "Aún no hay comentarios."}
                </Typography>
              ) : (
                comments.map((request) => {
                  if (request.isEvent) {
                    return (
                      <Box
                        key={request.id}
                        sx={{
                          px: 2.5,
                          py: 1,
                          borderBottom: "1px solid #F1F5F9",
                          backgroundColor: "#F8FAFC",
                          textAlign: "center",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", lineHeight: 1.5 }}>
                          {request.text}
                          <Box component="span" sx={{ color: "#9CA3AF" }}>
                            {" · "}
                            {formatRelative(request.created_at)}
                          </Box>
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box
                      key={request.id}
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        px: 2.5,
                        py: 1.75,
                        borderBottom: "1px solid #F1F5F9",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          minWidth: 32,
                          borderRadius: "50%",
                          background: request.sender?.role === "client" ? brand.gradientDiagonal : "#0F172A",
                          color: "#fff",
                          fontSize: "0.6875rem",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {initials(request.sender?.full_name || request.author_display_name)}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827" }}>
                            {request.sender?.full_name || request.author_display_name || "Cliente"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#9CA3AF" }}>
                            {formatRelative(request.created_at)}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "0.875rem", color: "#374151", mt: 0.5, lineHeight: 1.55, whiteSpace: "pre-line" }}>
                          {request.text}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderTop: "1px solid #F1F5F9" }}>
              <Box
                component="input"
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    handleSendReply();
                  }
                }}
                disabled={isDraft || !threadId || isSendingReply}
                placeholder={
                  isDraft
                    ? "Disponible cuando se envíe al cliente"
                    : isClient
                      ? "Escribe un comentario o pide un cambio..."
                      : "Responder al cliente..."
                }
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  fontSize: "0.875rem",
                  fontFamily: "inherit",
                  py: 1,
                  backgroundColor: "transparent",
                }}
              />
              <IconButton
                aria-label="Enviar"
                onClick={handleSendReply}
                disabled={!reply.trim() || isDraft || isSendingReply}
                sx={{
                  backgroundColor: brand.inkFill,
                  color: brand.inkFillText,
                  "&:hover": { backgroundColor: brand.inkHover },
                  width: 36,
                  height: 36,
                  "&.Mui-disabled": { backgroundColor: "#CBD5E1", color: "#fff" },
                }}
              >
                <SendHorizontal size={16} />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ ...cardShellSx, px: 2.5, py: 2 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A", mb: 1.25 }}>
              Historial de versiones · {platformName}
            </Typography>
            {isLoadingVersions ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={34} />
                ))}
              </Box>
            ) : versions.length === 0 ? (
              <Typography sx={{ fontSize: "0.875rem", color: "#6B7280" }}>Sin versiones registradas.</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, maxHeight: 260, overflowY: "auto" }}>
                {versions.map((version, versionIndex) => {
                  const isCurrent = versionIndex === 0;
                  const isBase = !isCurrent && baseVersion?.id === version.id;
                  return (
                    <Box key={version.id} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          minWidth: 8,
                          mt: 0.875,
                          borderRadius: "50%",
                          backgroundColor: isCurrent ? brand.pink : "#CBD5E1",
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#111827" }}>
                          v{version.version_number} · {versionLabel(version)}
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>
                          {version.created_by_name} · {formatShortDate(version.created_at, true)}
                        </Typography>
                      </Box>
                      {isCurrent ? (
                        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: brand.pink }}>Actual</Typography>
                      ) : (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setCompareBaseId(isBase ? null : version.id)}
                          sx={{ ...linkButtonSx, color: isBase ? "#0F172A" : brand.pink }}
                        >
                          {isBase ? "Comparando" : "Comparar"}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {mainAction}
        </Box>
        )}
      </Box>


    </Box>
  );
}
